/**
 * Descriptions for bars the PRECOMPUTED timeline marked.
 *
 * <h2>Why this exists at all</h2>
 *
 * The precomputed CTS files carry one boolean per day — {@code {"date":"1801-01-01",
 * "isReversal":false}} — and nothing else. That is enough to say which bars to mark and completely
 * silent about what is standing on them, so a chart drawn from those files has no confluence index
 * to write on a triangle and no configuration to name.
 *
 * <p>Re-running the whole scan to recover the detail would throw away the reason the timeline
 * exists: 300 years of days against the few hundred a chart actually marks. So only the marked days
 * are sent back, and the backend walks those. A 45-year chart asks about roughly 400 dates rather
 * than 17,000.
 *
 * <p>Every failure here degrades to unlabelled arrows rather than to no arrows. The marks are the
 * study; the labels describe them. Losing the description must not lose the finding.
 */

/**
 * The ceiling on one classification request.
 *
 * <h2>Raised from 2,000 to 10,000, and a note on what it is NOT</h2>
 *
 * Ten thousand covers forty years of trading days, so a full-history chart is classified in one
 * request rather than a window of it. At 2,000 the request covered about eight years and the rest
 * of the chart came back unclassified — indistinguishable from the backend having no opinion.
 *
 * <p><b>This was never the cause of the chart truncation</b>, and saying so matters because it
 * was suspected twice. {@code datesToClassify} has no callers at all; the truncation was
 * {@code MAX_DRAWN_TRADES}, measured. Raised anyway because the same defect is latent here and
 * would appear the moment something did call it.
 */
export const MAX_CLASSIFY_DATES = 10000;

/**
 * The distinct calendar dates behind a set of marked bars, in order, capped.
 *
 * <p>Distinct, because a scan that marks a Saturday and a Sunday resolves both onto the Friday and
 * asking twice about one date wastes a walk. Ordered, so the request is reproducible.
 *
 * <h2>A TRUNCATED request keeps the MOST RECENT dates, and that is a change</h2>
 *
 * It kept the EARLIEST, on the reasoning that a reproducible subset beats an arbitrary one. Both
 * are reproducible; only one is useful. Keeping the earliest means that on a long history the
 * classification simply stops partway along the chart and every recent bar comes back unclassified
 * — which is indistinguishable from the backend having no opinion about them, and it silently
 * blanks exactly the end of the chart a desk is looking at.
 *
 * <p>Truncation is also REPORTED now, for the same reason: dropping 3,000 of 5,000 dates without
 * saying so makes a rendering gap look like an absence of signal.
 */
export function datesToClassify(hits, max = MAX_CLASSIFY_DATES) {
  const seen = [];
  (Array.isArray(hits) ? hits : []).forEach((hit) => {
    const list = hit && Array.isArray(hit.dates) ? hit.dates : [];
    list.forEach((d) => {
      if (typeof d === 'string' && d && !seen.includes(d)) seen.push(d);
    });
  });
  if (seen.length <= max) return seen;
  console.warn(`[cts] ${seen.length} dates to classify exceeds the ${max} ceiling - keeping the `
    + `most recent ${max}; the ${seen.length - max} earliest will be unclassified`);
  return seen.slice(-max);
}

/** The classify response indexed by date, ignoring days the backend said do not qualify. */
export function indexClassified(days) {
  const byDate = new Map();
  (Array.isArray(days) ? days : []).forEach((day) => {
    if (!day || typeof day.date !== 'string') return;
    // qualifies:false is a real answer - the date was asked about and the sky did not hold - and it
    // carries no description, so indexing it would put an empty row where "unknown" belongs.
    if (day.qualifies === false) return;
    byDate.set(day.date, day);
  });
  return byDate;
}

/**
 * Which day describes a bar, when several snapped onto it.
 *
 * <p>The TIGHTEST, which is the same rule the live path's merge uses. A bar carrying one exact
 * configuration and one loose one should be described by the exact one — and more importantly, the
 * two paths must agree, or the same bar would carry a different index depending on whether the
 * dataset happened to be cached.
 */
export function dayForHit(hit, byDate) {
  const list = hit && Array.isArray(hit.dates) ? hit.dates : [];
  let best = null;
  list.forEach((d) => {
    const day = byDate && byDate.get ? byDate.get(d) : null;
    if (!day) return;
    const orb = typeof day.tightestOrb === 'number' ? day.tightestOrb : Infinity;
    const bestOrb = best && typeof best.tightestOrb === 'number' ? best.tightestOrb : Infinity;
    if (!best || orb < bestOrb) best = day;
  });
  return best;
}

/**
 * Fetches descriptions for the marked bars.
 *
 * <p>Returns an empty map on any failure — a refusal, a network error, an older backend that has
 * never heard of the endpoint. The caller then draws exactly what it drew before this existed,
 * which is the behaviour to fall back to: unlabelled arrows are a smaller loss than a study that
 * refuses to run because its captions are unavailable.
 *
 * @param {{post: Function}} api the axios-like client
 * @param {Array<{dates: string[]}>} hits the marked bars
 * @param {number} orb the orb the marks were produced at — the description must be made under the
 *                     same tolerance, or a bar would be labelled with a configuration that does not
 *                     exist at the orb it was marked under
 */
export async function fetchClassification(api, hits, orb, zodiac = 'geo-sidereal',
                                           peaks = null) {
  const dates = datesToClassify(hits);
  if (dates.length === 0 || !api || typeof api.post !== 'function') return new Map();
  /*
   * THE INSTANT TO SAMPLE EACH DATE AT, for the dates we have one for.
   *
   * Only the scanned span has them, so this map is normally a subset of `dates` -- a date with no
   * entry is sampled at midnight, which is the right answer for the years where no 30-minute scan
   * exists. Sent as an object rather than a parallel array so a reordering cannot misalign a date
   * with somebody else's instant, which would describe the wrong sky and look entirely plausible.
   */
  const at = {};
  if (peaks && typeof peaks.get === 'function') {
    dates.forEach((d) => {
      const peak = peaks.get(d);
      if (typeof peak === 'string' && peak) at[d] = peak;
    });
  }
  try {
    const res = await api.post('/api/gann-confluence/classify',
      Object.keys(at).length > 0 ? { dates, at, orb, zodiac } : { dates, orb, zodiac });
    const data = (res && res.data) || {};
    if (data.ok === false) {
      console.warn('[App] CTS classify refused:', data.error);
      return new Map();
    }
    return indexClassified(data.days);
  } catch (e) {
    console.warn('[App] CTS classify unavailable, drawing unlabelled marks:', e);
    return new Map();
  }
}
